<template>
    <div>
        <table class="kgr-table kgr-table--striped table-groups kgr-row roles kgr-table--block">
            <thead>
                <tr class="kgr-table__row">
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('clubEmployeeRole') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('Start') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('Ende') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('clubEmployeeRole.department') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('clubEmployeeRole.team') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline notice-cell">
                        {{ $t('Bemerkungen') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline"></th>
                </tr>
            </thead>
            <tbody>
                <template
                    v-for="role in roles"
                    :key="'row'+role.id"
                >
                    <kgr-club-employee-role-edit-row
                        :role="role"
                        :edit-roles="editRoles"
                        @remove="remove(role)"
                    />
                    <kgr-club-employee-role-show-row
                        :role="role"
                        :edit-roles="editRoles"
                        @edit="edit(role)"
                        @remove="remove(role)"
                    />
                </template>

                <tr
                    v-if="isCreatable"
                    class="kgr-table__row"
                >
                    <td class="kgr-table__cell">
                        <kgr-club-employee-role-select
                            v-model="newRole.parameterValue"
                            :club-id="clubId"
                        />
                    </td>
                    <td class="kgr-table__cell">
                        <div class="form-widget without-label date-picker">
                            <kgr-date-picker v-model="newRole.from" />
                        </div>
                    </td>
                    <td class="kgr-table__cell">
                        <div class="form-widget without-label date-picker">
                            <kgr-date-picker v-model="newRole.to" />
                        </div>
                    </td>
                    <td class="kgr-table__cell">
                        <kgr-department-select
                            :key="'department' + selectResetKey"
                            ref="departmentSelect"
                            :value="newRole.department"
                            hide-label
                            :club-id="clubId"
                            exclude-if-basic-data-not-viewable
                            @post-input="onDepartmentChange"
                            @optionsLoaded="departmentOptionsLoaded"
                        />
                    </td>
                    <td class="kgr-table__cell">
                        <kgr-team-select
                            :key="'team' + selectResetKey"
                            ref="teamSelect"
                            :value="newRole.team"
                            hide-label
                            :department="newRole.department"
                            :club-id="clubId"
                            @post-input="onTeamChange"
                        />
                    </td>
                    <td class="kgr-table__cell">
                        <div class="form-widget without-label date-picker">
                            <textarea
                                v-model.trim="newRole.notice"
                                class="kgr-input"
                                rows="3"
                            ></textarea>
                        </div>
                    </td>
                    <td class="kgr-table__cell">
&nbsp;
                    </td>
                </tr>
            </tbody>
        </table>
        <kgr-button
            v-if="showButton"
            class="button_save"
            :value="$t('saveChanges')"
            :disabled="!isSavable()"
            :pending="pendingCreate || pendingUpdate"
            @click="save()"
        />
    </div>
</template>

<script setup>
import { computed, getCurrentInstance, onMounted, ref } from 'vue';
import ClubEmployeeRepository from '../../../js/repository/ClubEmployeeRepository';
import ClubEmployeeRole from '../../../js/dto/ClubEmployeeRole';
import DepartmentRepository from '../../../js/repository/DepartmentRepository';
import KgrButton from '../../common/form/button/KgrButton';
import KgrClubEmployeeRoleEditRow from './ClubEmployeeRoleEditRow';
import KgrClubEmployeeRoleSelect from './ClubEmployeeRoleSelect';
import KgrClubEmployeeRoleShowRow from './ClubEmployeeRoleShowRow';
import KgrDatePicker from '../../common/form/picker/DatePicker';
import KgrDepartmentSelect from '../../common/form/select/DepartmentSelect.vue';
import KgrTeamSelect from '../../common/form/select/TeamSelect.vue';
import TeamRepository from '../../../js/repository/TeamRepository';

const HTTP_STATUS_CONFLICT = 409;
const OVERLAP_ERROR = 'Diese Funktion gibt es in der Kombination mit Abteilung und/oder Team bereits in einem sich überschneidenden Zeitraum.';

defineOptions({
    compatConfig: {
        MODE: 3,
    },
});

const props = defineProps({
    roles: {
        type: Array,
        required: true,
    },
    clubId: {
        type: Number,
        required: true,
    },
    employeeId: {
        type: Number,
        required: true,
    },
    isCreatable: {
        type: Boolean,
        required: true,
    },
});

const emit = defineEmits(['updated']);

const { proxy } = getCurrentInstance();

const newRole = ref(new ClubEmployeeRole());
const editRoles = ref([]);
const departments = ref([]);
const teams = ref([]);
const pendingCreate = ref(false);
const pendingUpdate = ref(false);
const departmentSelect = ref(null);
const selectResetKey = ref(0);

const showButton = computed(() => props.isCreatable || editRoles.value.length > 0);

onMounted(() => {
    updateDepartments();
    updateTeams();
});

function isSavable() {
    if (editRoles.value.length === 0 && newRole.value.isEmpty()) {
        return false;
    }

    if (editRoles.value.filter(editRole => !editRole.isValid()).length > 0) {
        return false;
    }

    if (!newRole.value.isEmpty() && !newRole.value.isValid()) {
        return false;
    }

    return true;
}

function remove(role) {
    showConfirm(proxy.$t('confirm'), proxy.$t('clubEmployeeRole.confirm.delete', { name: role.parameterValue.value }), () => {
        editRoles.value = editRoles.value.filter(r => r.id !== role.id);
        ClubEmployeeRepository
            .removeClubEmployeeRole(props.employeeId, role.id)
            .then(() => {
                proxy.$notify.success('clubEmployeeRole.delete.success', { name: role.parameterValue.value });
                emit('updated');
            })
            .catch(() => proxy.$notify.error('saveChanges.error'));
    }, 'Ok');
}

function edit(role) {
    editRoles.value.push(role);
}

function save() {
    if (editRoles.value.length > 0) {
        update();
    }

    if (!newRole.value.isEmpty()) {
        create();
    }
}

function create() {
    if (newRole.value.department && !newRole.value.department.id) {
        newRole.value.department = null;
    }
    if (newRole.value.team && !newRole.value.team.id) {
        newRole.value.team = null;
    }
    if (checkOverlappingRole(newRole.value)) {
        proxy.$notify.error(OVERLAP_ERROR, { name: newRole.value.parameterValue.value });
        return;
    }

    pendingCreate.value = true;
    ClubEmployeeRepository
        .createClubEmployeeRole(props.employeeId, newRole.value)
        .then(() => {
            newRole.value = new ClubEmployeeRole();
            selectResetKey.value++;
            updateTeams();
            updateDepartments();
            proxy.$notify.success('saveChanges.success');
            emit('updated');
        })
        .catch(error => {
            if (error.status === HTTP_STATUS_CONFLICT) {
                proxy.$notify.error(OVERLAP_ERROR, { name: newRole.value.parameterValue.value });
            } else {
                proxy.$notify.error('saveChanges.error');
            }
        })
        .finally(() => {
            pendingCreate.value = false;
        });
}

function checkOverlappingRole(role) {
    let overlappingRoles = false;
    props.roles.forEach(dataRole => {
        if (role.id !== dataRole.id && role.parameterValue.value === dataRole.parameterValue.value) {
            overlappingRoles = overlappingRoles || role.overlaps(dataRole);
        }
    });
    return overlappingRoles;
}

function update() {
    let overlappingRole = null;
    editRoles.value.forEach(role => {
        overlappingRole = checkOverlappingRole(role) ? role : overlappingRole;
    });
    if (overlappingRole) {
        proxy.$notify.error(OVERLAP_ERROR, { name: overlappingRole.parameterValue.value });
        return;
    }

    pendingUpdate.value = true;
    ClubEmployeeRepository
        .updateClubEmployeeRoles(props.employeeId, editRoles.value)
        .then(() => {
            editRoles.value = [];
            proxy.$notify.success('saveChanges.success');
            emit('updated');
        })
        .catch(() => proxy.$notify.error('saveChanges.error'))
        .finally(() => {
            pendingUpdate.value = false;
        });
}

function updateDepartments() {
    if (newRole.value.team) {
        newRole.value.department = getDepartmentById(newRole.value.team.departmentId);
        reloadPicker();
    } else {
        DepartmentRepository.findAll(props.clubId)
            .then(loadedDepartments => {
                departments.value = loadedDepartments.filter(department => department.isViewable);
            })
            .catch(() => proxy.$notify.error('error.loadingData'));
    }
}

function onDepartmentChange(department) {
    newRole.value.department = department;
    if (newRole.value.team && department && newRole.value.team.departmentId === department.id) {
        return;
    }
    updateTeams();
}

function onTeamChange(team) {
    newRole.value.team = team;
    updateDepartments();
}

function departmentOptionsLoaded(loadedDepartments) {
    departments.value = loadedDepartments;
}

function updateTeams() {
    newRole.value.team = null;
    if (newRole.value.department) {
        DepartmentRepository.findTeams(newRole.value.department.id)
            .then(loadedTeams => {
                teams.value = loadedTeams;
            })
            .catch(() => proxy.$notify.error('error.loadingData'));
    } else {
        TeamRepository.findAll(props.clubId)
            .then(loadedTeams => {
                teams.value = loadedTeams;
            })
            .catch(() => proxy.$notify.error('error.loadingData'));
    }
}

function getDepartmentById(id) {
    if (!Array.isArray(departments.value)) {
        return null;
    }
    return departments.value.find(department => department.id === id) ?? null;
}

function reloadPicker() {
    departmentSelect.value.reload();
}
</script>

<style scoped lang="scss" rel="stylesheet/scss">
    .button_save {
        display: block;
        margin-left: auto;
    }

    div .date-picker {
        border: 1px solid transparent;
    }

    .notice-cell {
        width: 30%;
    }
</style>
